/**
 * TerminologyPage - Terminology Override Management
 *
 * Surfaces the terminologyOverrides query and approveTerminologyOverride mutation.
 * Allows architects to review and approve terminology override requests.
 *
 * Route: /admin/terminology
 *
 * DRR-238 Phase 2: Surfacing DEP resolvers
 * DRR-189: Statutory definitions and terminology governance
 */

import React, { useState } from 'react';
import {
  Container,
  Title,
  Group,
  Badge,
  Text,
  ThemeIcon,
  Card,
  Table,
  Button,
  Modal,
  Textarea,
  Stack,
  LoadingOverlay,
  Alert,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconLanguage,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconClock,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

import { useAuth } from '@/auth/AuthContext';
import { OverrideStatus } from '@/generated/graphql';

// =============================================================================
// GraphQL Operations
// =============================================================================

const TERMINOLOGY_OVERRIDES = gql`
  query TerminologyOverrides {
    terminologyOverrides {
      definitionId
      proposedTerm
      classification
      status
      rationale
      requestedBy
      requestedAt
      decidedBy
      decidedAt
      decisionRationale
      violatedConstraint
      expiresAt
    }
  }
`;

const APPROVE_TERMINOLOGY_OVERRIDE = gql`
  mutation ApproveTerminologyOverride($definitionId: String!, $rationale: String!) {
    approveTerminologyOverride(definitionId: $definitionId, rationale: $rationale) {
      definitionId
      status
      decidedBy
      decidedAt
      decisionRationale
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

interface TerminologyOverride {
  definitionId: string;
  proposedTerm: string;
  classification: string;
  status: OverrideStatus;
  rationale: string | null;
  requestedBy: string;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionRationale: string | null;
  violatedConstraint: string | null;
  expiresAt: string | null;
}

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS: Record<OverrideStatus, string> = {
  [OverrideStatus.PENDING_CONFERENCE]: 'yellow',
  [OverrideStatus.ACTIVE]: 'green',
  [OverrideStatus.DENIED]: 'red',
  [OverrideStatus.EXPIRED]: 'gray',
};

const CLASSIFICATION_COLORS: Record<string, string> = {
  STATUTORY: 'blue',
  INDUSTRY: 'cyan',
  INTERNAL: 'gray',
  CUSTOM: 'orange',
};

// =============================================================================
// Component
// =============================================================================

export function TerminologyPage() {
  const { tenant, user } = useAuth();
  const [selectedOverride, setSelectedOverride] = useState<TerminologyOverride | null>(null);
  const [approvalRationale, setApprovalRationale] = useState('');
  const [approveModalOpened, { open: openApproveModal, close: closeApproveModal }] = useDisclosure(false);

  // GraphQL
  const [loadOverrides, { data, loading, error, refetch }] = useLazyQuery<{
    terminologyOverrides: TerminologyOverride[];
  }>(TERMINOLOGY_OVERRIDES);

  const [approveOverride, { loading: approving }] = useMutation(APPROVE_TERMINOLOGY_OVERRIDE);

  // Load on mount
  React.useEffect(() => {
    loadOverrides();
  }, [loadOverrides]);

  const handleApprove = async () => {
    if (!selectedOverride) return;

    try {
      await approveOverride({
        variables: {
          definitionId: selectedOverride.definitionId,
          rationale: approvalRationale,
        },
      });
      closeApproveModal();
      setApprovalRationale('');
      setSelectedOverride(null);
      refetch();
    } catch (err) {
      console.error('Failed to approve override:', err);
    }
  };

  const openApproval = (override: TerminologyOverride) => {
    setSelectedOverride(override);
    openApproveModal();
  };

  const overrides = data?.terminologyOverrides ?? [];
  const pendingCount = overrides.filter((o) => o.status === OverrideStatus.PENDING_CONFERENCE).length;

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="violet">
            <IconLanguage size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>Terminology Overrides</Title>
            <Text size="sm" c="dimmed">
              Review and approve terminology override requests
            </Text>
          </div>
        </Group>
        <Group>
          {pendingCount > 0 && (
            <Badge color="yellow" variant="light" size="lg">
              {pendingCount} pending
            </Badge>
          )}
          {tenant && (
            <Badge variant="light" color="gray">
              {tenant.name}
            </Badge>
          )}
        </Group>
      </Group>

      <Card withBorder pos="relative" mih={300}>
        <LoadingOverlay visible={loading} />

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
            {error.message}
          </Alert>
        )}

        {overrides.length > 0 ? (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Definition ID</Table.Th>
                <Table.Th>Proposed Term</Table.Th>
                <Table.Th>Classification</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Requested By</Table.Th>
                <Table.Th>Requested At</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {overrides.map((override) => (
                <Table.Tr key={override.definitionId}>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {override.definitionId}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {override.proposedTerm}
                    </Text>
                    {override.rationale && (
                      <Text size="xs" c="dimmed">
                        {override.rationale}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={CLASSIFICATION_COLORS[override.classification] ?? 'gray'}
                      variant="light"
                      size="sm"
                    >
                      {override.classification}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={STATUS_COLORS[override.status] ?? 'gray'}
                      variant="light"
                    >
                      {override.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{override.requestedBy}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {new Date(override.requestedAt).toLocaleDateString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {override.status === OverrideStatus.PENDING_CONFERENCE && (
                      <Group gap="xs">
                        <Tooltip label="Approve">
                          <ActionIcon
                            color="green"
                            variant="light"
                            onClick={() => openApproval(override)}
                          >
                            <IconCheck size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    )}
                    {override.status === OverrideStatus.ACTIVE && (
                      <Tooltip label={`Approved by ${override.decidedBy}`}>
                        <ThemeIcon color="green" variant="light" size="sm">
                          <IconShieldCheck size={14} />
                        </ThemeIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <Stack align="center" py="xl">
            <IconLanguage size={48} color="gray" opacity={0.5} />
            <Text c="dimmed">No terminology overrides found</Text>
          </Stack>
        )}
      </Card>

      {/* Approval Modal */}
      <Modal
        opened={approveModalOpened}
        onClose={closeApproveModal}
        title="Approve Terminology Override"
      >
        {selectedOverride && (
          <Stack>
            <div>
              <Text size="sm" fw={500}>
                Definition ID
              </Text>
              <Text size="sm" c="dimmed" ff="monospace">
                {selectedOverride.definitionId}
              </Text>
            </div>
            <div>
              <Text size="sm" fw={500}>
                Proposed Term
              </Text>
              <Text size="sm">{selectedOverride.proposedTerm}</Text>
            </div>
            {selectedOverride.violatedConstraint && (
              <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                <Text size="sm">
                  <strong>Constraint violated:</strong> {selectedOverride.violatedConstraint}
                </Text>
              </Alert>
            )}
            <Textarea
              label="Approval Rationale"
              placeholder="Explain why this override is approved..."
              value={approvalRationale}
              onChange={(e) => setApprovalRationale(e.currentTarget.value)}
              minRows={3}
              required
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={closeApproveModal}>
                Cancel
              </Button>
              <Button
                color="green"
                onClick={handleApprove}
                loading={approving}
                disabled={!approvalRationale.trim()}
                leftSection={<IconCheck size={16} />}
              >
                Approve
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
}

export default TerminologyPage;
